import { Component, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  first,
  map,
  Observable,
  shareReplay,
  Subject,
  tap,
} from 'rxjs';
import { Organization } from 'src/app/core/models/organization/organization.interface';
import { OrganizationListItemView } from 'src/app/core/models/organization/organization-list-item-view.interface';
import { PagedList } from 'src/app/core/models/paged-list.interface';
import { RequestModel } from 'src/app/core/models/request-model.interface';
import { OrganizationService } from 'src/app/core/services/organization.service';
import { TranslateService } from '@ngx-translate/core';
import { Constants } from 'src/app/core/constants';
import { PermissionService } from 'src/app/core/services/permission.service';
import { ResourceTypes } from 'src/app/core/enums/resource-types.enum';
import { UserCompanyPermissions } from 'src/app/core/enums/user-company-permissions.enum';
import { EmployeeProfileComponent } from '../../employees/employee-profile/employee-profile.component';
import { SortChangeEvent } from 'src/app/core/directives/sortable-header.directive';

@Component({
    selector: 'app-organizations',
    templateUrl: './organizations.component.html',
    styleUrl: './organizations.component.scss',
    standalone: false
})
export class OrganizationsComponent {
  private readonly modalService = inject(NgbModal);
  private readonly organizationService = inject(OrganizationService);
  private readonly permissionService = inject(PermissionService);
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);

  public readonly currentLang: string;

  public readonly emptyOrganizationListParam: Record<string, string> = {
    uk: 'організацій',
    en: 'organization',
  };

  public readonly canCreateOrganization$ =
    this.permissionService.userPermissions$.pipe(
      map((permissions) =>
        this.permissionService.hasPermission(
          permissions,
          ResourceTypes.Organization,
          UserCompanyPermissions.Create,
        ),
      ),
      shareReplay(),
    );

  public companyId = '';
  public isLoading = false;
  public isCreateOrganizationModalOpen = false;
  public searchTerm = '';

  public organizations$: Observable<PagedList<OrganizationListItemView>> = EMPTY;
  private readonly searchChanges$ = new Subject<string>();

  private request: RequestModel = {
    page: 1,
    pageSize: 10,
    filters: [],
    sortColumn: 'title',
    sortOrder: 'asc',
  };

  constructor() {
    const route = inject(ActivatedRoute);

    route.parent?.parent?.params
      .pipe(
        first(),
        tap((params) => {
          this.companyId = params['id'];
          this.organizationService.setOrganizationsUrl(this.companyId);
          this.isLoading = true;
        }),
        tap(() => this.loadOrganizations()),
      )
      .subscribe();

    this.currentLang =
      this.translateService.currentLang ?? Constants.DefaultLanguage;

    this.searchChanges$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((value) => {
          this.request.page = 1;
          this.request.searchTerm = value.trim() || undefined;
          this.isLoading = true;
          this.loadOrganizations();
        }),
      )
      .subscribe();
  }

  public openCreateOrganizationModal(): void {
    this.isCreateOrganizationModalOpen = true;
  }

  public openImport(): void {
    if (!this.companyId) {
      return;
    }

    void this.router.navigate(['/companies', this.companyId, 'settings'], {
      queryParams: {
        section: 'imports',
        importEntity: 'organizations',
      },
    });
  }

  public closeCreateOrganizationModal(): void {
    this.isCreateOrganizationModalOpen = false;
  }

  public onCreateOrganization(): void {
    this.closeCreateOrganizationModal();
    this.isLoading = true;
    this.loadOrganizations();
  }

  public onPageChange(page: number): void {
    this.request.page = page;
    this.loadOrganizations();
  }

  public onChangePageSize(size: number): void {
    this.request.pageSize = size;
    this.request.page = 1;
    this.loadOrganizations();
  }

  public onSearchChange(value: string): void {
    this.searchTerm = value;
    this.searchChanges$.next(value);
  }

  public onSortChange(event: SortChangeEvent): void {
    this.request.sortColumn = event.column;
    this.request.sortOrder = event.order;
    this.request.page = 1;
    this.isLoading = true;
    this.loadOrganizations();
  }

  public get activeSortColumn(): string {
    return this.request.sortColumn ?? '';
  }

  public get activeSortOrder(): 'asc' | 'desc' {
    return this.request.sortOrder ?? 'asc';
  }

  public viewProfile(employeeId: string): void {
    if (!employeeId) {
      return;
    }

    const modalRef = this.modalService.open(EmployeeProfileComponent, {
      size: 'lg',
      windowClass: 'br-50',
    });

    modalRef.componentInstance.id = employeeId;
    modalRef.componentInstance.showDeleteButton = false;
  }
  private loadOrganizations(): void {
    this.organizations$ = this.organizationService
      .getCompanyOrganizationsPaged(this.request)
      .pipe(
        map((page) => ({
          ...page,
          items: page.items.map((organization) =>
            this.mapOrganizationToListItemView(organization),
          ),
        })),
        tap(() => (this.isLoading = false)),
        catchError((error) => {
          this.isLoading = false;
          throw error;
        }),
        shareReplay(1),
      );
  }

  private mapOrganizationToListItemView(
    organization: Organization,
  ): OrganizationListItemView {
    const entityType = this.getEntityType(organization);

    return {
      id: organization.id,
      title: organization.title,
      address: organization.address ?? '',
      location: [organization.city, organization.country]
        .filter((value): value is string => Boolean(value?.trim()))
        .join(', '),
      contactsCount: organization.contactsCount,
      email: organization.email ?? '',
      phoneNumber: organization.phoneNumber ?? '',
      responsibleUserId: organization.responsibleUserId,
      responsibleUserName: organization.responsibleUserName ?? '',
      hasResponsibleUser: Boolean(organization.responsibleUserId),
      entityType,
      entityTypeTitle: this.translateService.instant(
        this.getEntityTypeTitleKey(entityType),
      ),
    };
  }

  private getEntityType(
    organization: Organization,
  ): 'regular' | 'customer' | 'vendor' {
    if (organization.isCustomer) {
      return 'customer';
    }

    if (organization.isVendor) {
      return 'vendor';
    }

    return 'regular';
  }

  private getEntityTypeTitleKey(
    entityType: 'regular' | 'customer' | 'vendor',
  ): string {
    if (entityType === 'customer') {
      return 'TOOLTIPS.ENTITY_TYPE_CUSTOMER';
    }

    if (entityType === 'vendor') {
      return 'TOOLTIPS.ENTITY_TYPE_VENDOR';
    }

    return 'TOOLTIPS.ENTITY_TYPE_REGULAR';
  }
}
